import { z } from 'zod';
import { PUSH_MAX_TTL_SECONDS, PUSH_PLATFORMS } from '../push/delivery-options.ts';
import { PushButtonId, PushExtraValue } from '../push/sdk-payload.ts';
import { LocaleCode, TRANSLATION_STATUSES } from './localization.ts';

/**
 * What a marketer writes for the push channels `push_android` / `push_ios` of a campaign (BB-73, PUSH-001): texts
 * with Liquid, per-platform overrides, the click action and buttons (semantics BB-74), media (BB-75), send settings
 * (BB-76) and extras (BB-58). Two composer modes: separate channels (Signal New: an Android and an iOS tab, each
 * content with one platform) or shared content (`platforms: ['android', 'ios']`) written into both channels. The
 * delivery renders only its own platform. Translations follow BB-55: `localized` overrides with statuses.
 */
const Template = z.string().max(4096);

/** A click action as written: its link may carry Liquid and is checked after rendering. */
export const PushActionDraft = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('open_app') }),
  z.strictObject({ kind: z.literal('deep_link'), uri: Template.min(1) }),
  z.strictObject({ kind: z.literal('web_url'), uri: Template.min(1), openIn: z.enum(['in_app', 'external']).default('external') }),
]);
export type PushActionDraft = z.infer<typeof PushActionDraft>;

const ButtonAction = z.union([PushActionDraft, z.strictObject({ kind: z.literal('dismiss') })]);

export const PushButtonDraft = z.strictObject({ id: PushButtonId, label: Template.min(1).max(256), action: ButtonAction });
export type PushButtonDraft = z.infer<typeof PushButtonDraft>;

export const PushSendSettings = z.strictObject({
  destination: z.enum(['all_devices', 'most_recent_device']).default('all_devices'),
  iosDeviceFamily: z.enum(['all', 'iphone', 'ipad']).default('all'),
  /** Only these app instances; all apps of the platform when unset. */
  appInstanceIds: z.array(z.uuid()).min(1).max(50).optional(),
  ttl: z
    .discriminatedUnion('mode', [z.strictObject({ mode: z.literal('environment_default') }), z.strictObject({ mode: z.literal('custom'), seconds: z.int().min(0).max(PUSH_MAX_TTL_SECONDS) })])
    .default({ mode: 'environment_default' }),
  androidPriority: z.enum(['environment_default', 'normal', 'high']).default('environment_default'),
  androidChannelId: z.string().min(1).max(64).optional(),
  androidNotificationId: z.union([z.strictObject({ fixed: z.int32() }), z.strictObject({ template: Template.min(1) })]).optional(),
  collapseKey: Template.min(1).max(512).optional(),
  iosInterruptionLevel: z.enum(['environment_default', 'passive', 'active', 'time-sensitive']).default('environment_default'),
  iosRelevanceScore: z.number().min(0).max(1).optional(),
  iosThreadId: z.string().min(1).max(64).optional(),
  iosBadge: z
    .discriminatedUnion('mode', [z.strictObject({ mode: z.literal('none') }), z.strictObject({ mode: z.literal('set'), value: Template.min(1) }), z.strictObject({ mode: z.literal('clear') })])
    .default({ mode: 'none' }),
  sound: z.enum(['environment_default', 'default', 'none']).default('environment_default'),
});
export type PushSendSettings = z.infer<typeof PushSendSettings>;

export const PushContent = z.strictObject({
  schemaVersion: z.literal(1).default(1),
  platforms: z
    .array(z.enum(PUSH_PLATFORMS))
    .min(1)
    .max(PUSH_PLATFORMS.length)
    .refine((list) => new Set(list).size === list.length, 'a platform is listed twice')
    .default(['android', 'ios']),
  /** `silent`: no text, wakes the app (sync hints, BB-82). */
  notificationType: z.enum(['standard', 'silent']).default('standard'),
  title: Template.optional(),
  body: Template.optional(),
  androidOverrides: z.strictObject({ title: Template.optional(), body: Template.optional(), summary: Template.optional() }).optional(),
  iosOverrides: z.strictObject({ title: Template.optional(), subtitle: Template.optional(), body: Template.optional() }).optional(),
  action: PushActionDraft.default({ kind: 'open_app' }),
  actionOverrides: z.strictObject({ android: PushActionDraft.optional(), ios: PushActionDraft.optional() }).optional(),
  androidButtons: z.array(PushButtonDraft).max(3).optional(),
  /** iOS buttons come from a category the app registered; the push carries their actions. */
  iosCategory: z.strictObject({ categoryId: z.string().min(1).max(64), buttonActions: z.record(PushButtonId, ButtonAction) }).optional(),
  media: z
    .strictObject({
      imageUrl: Template.min(1).optional(),
      androidImageAssetId: z.uuid().optional(),
      androidLargeIconAssetId: z.uuid().optional(),
      iosImageAssetId: z.uuid().optional(),
    })
    .optional(),
  settings: PushSendSettings.prefault({}),
  /** Raw key-value pairs for the host app (`bb.ex`). */
  extras: z.record(z.string().min(1).max(100), PushExtraValue).default({}),
  localized: z.record(LocaleCode, z.record(z.string(), z.unknown())).optional(),
  translationStatus: z.record(LocaleCode, z.enum(TRANSLATION_STATUSES)).optional(),
});
export type PushContent = z.infer<typeof PushContent>;
export type PushContentInput = z.input<typeof PushContent>;

/** Recommended lengths in graphemes (Braze push docs): beyond them the text may be cut on the device. */
export const PUSH_LENGTH_GUIDANCE = [
  { platform: 'ios', surface: 'lock_screen', field: 'body', graphemes: 160, withImage: 130 },
  { platform: 'ios', surface: 'banner', field: 'body', graphemes: 80, withImage: 65 },
  { platform: 'android', surface: 'lock_screen', field: 'body', graphemes: 49, withImage: 49 },
  { platform: 'android', surface: 'drawer_expanded', field: 'body', graphemes: 597, withImage: 597 },
] as const;

export interface PushContentIssue {
  code: string;
  path: string;
  message: string;
  platform?: 'android' | 'ios';
}

/** `POST …/push/validate`: problems that block publishing, hints, lengths and payload size per platform. */
export interface PushValidationResult {
  errors: PushContentIssue[];
  warnings: PushContentIssue[];
  lengths: Array<{ platform: 'android' | 'ios'; field: string; graphemes: number; limits: Array<{ surface: string; graphemes: number; exceeded: boolean }> }>;
  estimatedPayloadBytes: { android?: number; ios?: number };
}

export const PushValidateBody = z.strictObject({ content: PushContent, category: z.enum(['marketing', 'transactional']).optional() });
export type PushValidateBody = z.infer<typeof PushValidateBody>;
